import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import { useAuth } from '../contexts/AuthContext';
import { fetchFriendActivities, formatGameActivityLabel } from '../lib/friendActivity';
import FriendActivitySetupNote from '../components/FriendActivitySetupNote';
import FriendActivitySummary from '../components/FriendActivitySummary';

export default function FriendsFeed() {
  const { user, syncSteamData } = useAuth();
  const [friends, setFriends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [expandedFriendId, setExpandedFriendId] = useState(null);

  const loadActivities = useCallback(async () => {
    if (!user?.id) return;

    setError(null);

    try {
      const grouped = await fetchFriendActivities(user.id);
      setFriends(grouped);
    } catch (err) {
      setError(err.message || 'Failed to load friend activity.');
    }
  }, [user?.id]);

  useEffect(() => {
    loadActivities().finally(() => setLoading(false));
  }, [loadActivities]);

  const handleRefresh = async () => {
    setRefreshing(true);
    setError(null);
    try {
      await syncSteamData();
      await loadActivities();
    } catch (err) {
      setError(err.message || 'Failed to refresh activity.');
    } finally {
      setRefreshing(false);
    }
  };

  const toggleFriend = (friendId) => {
    setExpandedFriendId((current) => (current === friendId ? null : friendId));
  };

  if (loading) {
    return (
      <Stack alignItems="center" justifyContent="center" className="min-h-[40vh]">
        <CircularProgress color="primary" />
      </Stack>
    );
  }

  return (
    <div className="w-full py-6 sm:py-8">
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={2}
        className="mb-6"
      >
        <div className="min-w-0">
          <Typography
            variant="h4"
            className="text-2xl font-bold text-slate-100 sm:text-3xl md:text-4xl"
          >
            Friends Activity
          </Typography>
          <Typography variant="body1" className="mt-1 text-slate-300">
            All mutual friends on GamingWithBros — tap for game details. Syncs every 5 minutes.
          </Typography>
        </div>
        <Button
          variant="outlined"
          startIcon={refreshing ? <CircularProgress size={16} /> : <RefreshIcon />}
          onClick={handleRefresh}
          disabled={refreshing}
          sx={{ alignSelf: { xs: 'stretch', sm: 'center' }, flexShrink: 0 }}
        >
          Refresh
        </Button>
      </Stack>

      <FriendActivitySetupNote />

      {error && (
        <Typography color="error" className="mb-4">
          {error}
        </Typography>
      )}

      {friends.length === 0 ? (
        <Card elevation={0}>
          <CardContent className="py-10 text-center">
            <SportsEsportsIcon className="mb-2 text-4xl text-indigo-400" />
            <Typography className="text-slate-300">
              No friend activity yet. Use the checklist above if you expected someone to show up.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-2">
          {friends.map((friend) => {
            const expanded = expandedFriendId === friend.friendId;

            return (
              <Card key={friend.friendId} elevation={0} className="h-fit w-full">
                <CardActionArea onClick={() => toggleFriend(friend.friendId)}>
                  <CardContent>
                    <FriendActivitySummary friend={friend} />
                    <Stack direction="row" alignItems="center" justifyContent="space-between" className="mt-3">
                      <Typography variant="body2" className="text-slate-400">
                        {friend.games.length === 0
                          ? 'No games in the last 2 weeks — tap for details'
                          : friend.games.length === 1
                            ? '1 recent game — tap for details'
                            : `${friend.games.length} recent games — tap for details`}
                      </Typography>
                      <ExpandMoreIcon
                        className="text-slate-400 transition-transform"
                        sx={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
                      />
                    </Stack>
                  </CardContent>
                </CardActionArea>

                <Collapse in={expanded} unmountOnExit>
                  <Divider />
                  <CardContent className="pt-4">
                    <Typography variant="subtitle2" className="mb-2 font-semibold text-slate-300">
                      Recent games
                    </Typography>
                    <Stack spacing={1.5}>
                      {friend.games.length === 0 ? (
                        <Typography variant="body2" className="text-slate-400">
                          Steam has not reported any games in the last 2 weeks for this friend.
                        </Typography>
                      ) : friend.games.map((game) => (
                        <Stack
                          key={game.app_id}
                          direction={{ xs: 'column', sm: 'row' }}
                          alignItems={{ xs: 'flex-start', sm: 'center' }}
                          justifyContent="space-between"
                          spacing={{ xs: 1, sm: 2 }}
                          className="rounded-lg bg-slate-900/40 px-3 py-2"
                        >
                          <Typography
                            className="w-full text-slate-200 sm:min-w-0 sm:flex-1"
                            sx={{ wordBreak: 'break-word' }}
                          >
                            {game.game_name}
                          </Typography>
                          <Stack
                            direction="row"
                            spacing={1}
                            useFlexGap
                            flexWrap="wrap"
                            sx={{ width: { xs: '100%', sm: 'auto' }, flexShrink: 0 }}
                          >
                            {game.is_currently_playing ? (
                              <Chip label="Playing now" size="small" color="success" />
                            ) : formatGameActivityLabel(game) ? (
                              <Chip
                                label={formatGameActivityLabel(game)}
                                size="small"
                                variant="outlined"
                              />
                            ) : null}
                          </Stack>
                        </Stack>
                      ))}
                    </Stack>
                  </CardContent>
                </Collapse>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
